// bigwaSearchStore.ts — Estado global do painel Bigwa Search (fatia 1: Leituras)
// Espelha o padrão do quickFindStore. Sem persistência: o painel nasce fechado a cada sessão.

import { create } from 'zustand';
// Type-only: o módulo markingsIndex é 100% dynamic import (zero bytes no entry).
import type { MarkingType } from '../services/markingsIndex/types';
// Type-only: vocabulário dos filtros vem dos tipos existentes do Work.
import type { Work, WorkType } from '../types/work';

export type BigwaSearchScope = 'mesa' | 'biblioteca';

// ---- Dimensões de refino (Sprint 031, fatia 3) ----
// Tema · Campos (ano/autor/tipo) · Estados (relevância/status/favorito) ·
// Meus Trabalhos. Default integral vazio/OFF = fatias 1+2 byte-a-byte.
// Sem persistência (sessão), como o restante do painel.

export type WorkRelevance = NonNullable<Work['relevance']>;

export interface BigwaSearchFilters {
  /** Tags semânticas selecionadas; pode conter o sentinel UNTAGGED_LABEL ("Sem tag"). */
  tema: string[];
  /** Faixa de ano de–até; extremidade única = aberta. */
  anoDe: number | null;
  anoAte: number | null;
  /** Substring normalizada (removeAccents) sobre os autores da obra. */
  autor: string;
  /** Multi-select OR sobre WorkType. */
  tipos: WorkType[];
  /** Multi-select OR sobre a relevância da obra. */
  relevancias: WorkRelevance[];
  /** Multi-select OR por id de status da lista viva do preferencesStore. */
  statuses: string[];
  favorito: boolean;
  /** myAuthorship ∈ {auto-detected, confirmed} — predicado de useLibraryData.ts:254 (cópia). */
  meusTrabalhos: boolean;
}

const makeDefaultFilters = (): BigwaSearchFilters => ({
  tema: [],
  anoDe: null,
  anoAte: null,
  autor: '',
  tipos: [],
  relevancias: [],
  statuses: [],
  favorito: false,
  meusTrabalhos: false,
});

/** Shape neutro de referência (testes/guardiões). O estado usa cópias frescas. */
export const DEFAULT_FILTERS: Readonly<BigwaSearchFilters> = makeDefaultFilters();

// ---- Toggles Incluir (Sprint 030, fatia 2) ----
// 6 toggles, default tudo OFF = comportamento da fatia 1 preservado por construção.
export type MarkingToggleKey =
  | 'destaques' | 'parafrases' | 'comentarios' | 'fichamentos' | 'notas' | 'pdfTexto';

export type IncludeToggles = Record<MarkingToggleKey, boolean>;

/**
 * Toggle → tipos do índice. 'nota-biblioteca' (comentário global, nome do
 * requirements) vive sob Comentários; 'ideia-central' sob Notas — os dois não
 * têm toggle próprio (resolução de 6 toggles) mas têm card e pouso (D2).
 */
export const MARKING_TOGGLE_TYPES: Record<MarkingToggleKey, readonly MarkingType[]> = {
  destaques: ['destaque'],
  parafrases: ['paráfrase'],
  comentarios: ['comentário', 'nota-biblioteca'],
  fichamentos: ['fichamento'],
  notas: ['nota', 'sticky', 'ideia-central'],
  pdfTexto: ['pdf-texto'],
};

const DEFAULT_TOGGLES: IncludeToggles = {
  destaques: false,
  parafrases: false,
  comentarios: false,
  fichamentos: false,
  notas: false,
  pdfTexto: false,
};

interface BigwaSearchState {
  isOpen: boolean;
  query: string;
  scope: BigwaSearchScope;
  exact: boolean;
  includeToggles: IncludeToggles;
  /** Dimensões de refino (Sprint 031) — default integral vazio/OFF. */
  filters: BigwaSearchFilters;
  /** Progresso do build da família B — exibido DENTRO do painel, nunca toast. */
  buildProgress: { done: number; total: number } | null;
  /** Índice desligado por quota (A6) — aviso discreto no painel. */
  indexDegraded: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setQuery: (query: string) => void;
  setScope: (scope: BigwaSearchScope) => void;
  setExact: (exact: boolean) => void;
  setIncludeToggle: (key: MarkingToggleKey, value: boolean) => void;
  setFilter: <K extends keyof BigwaSearchFilters>(key: K, value: BigwaSearchFilters[K]) => void;
  clearFilters: () => void;
  setBuildProgress: (p: { done: number; total: number } | null) => void;
  setIndexDegraded: (value: boolean) => void;
  clear: () => void;
}

export const useBigwaSearchStore = create<BigwaSearchState>((set) => ({
  isOpen: false,
  query: '',
  scope: 'mesa',
  exact: false,
  includeToggles: { ...DEFAULT_TOGGLES },
  filters: makeDefaultFilters(),
  buildProgress: null,
  indexDegraded: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  setQuery: (query) => set({ query }),
  setScope: (scope) => set({ scope }),
  setExact: (exact) => set({ exact }),
  setIncludeToggle: (key, value) =>
    set((state) => ({ includeToggles: { ...state.includeToggles, [key]: value } })),
  setFilter: (key, value) =>
    set((state) => ({ filters: { ...state.filters, [key]: value } })),
  clearFilters: () => set({ filters: makeDefaultFilters() }),
  setBuildProgress: (buildProgress) => set({ buildProgress }),
  setIndexDegraded: (indexDegraded) => set({ indexDegraded }),
  // Limpar a busca ≠ fechar o painel (AS12): esvazia a query E as dimensões
  // de refino (Sprint 031), mantém isOpen. Toggles Incluir seguem a 030.
  clear: () => set({ query: '', filters: makeDefaultFilters() }),
}));
